import React from 'react';
import Link from 'next/link';
import { GlobalResponse, Slug } from '../../models/domain/interfaces';
import {
  TableBody,
  TableBodyRow,
  TableBodyRowCell,
  TableHeader,
} from '../../components/table/Table';
import Pagination from '../../components/pagination/Pagination';
import { PaginationItem } from '@mui/material';
import PageSelect from '../../components/page-select/PageSelect';

import {
  Label,
  MainPagination,
  StyledHeader,
  StyledLink,
  StyledSubtitle,
  TableStyled,
} from './NewsArchive.style';
import Markdown from '../../components/markdown/Markdown';
import { ContentWrapper } from '../../styles/shared';
import { useRouter } from 'next/router';
import { getNews } from '../../utils/api';
import normalizeText from '../../utils/normalizeText';
import { normalizeAttributesNewsEvents } from '../../utils/event';
import { Newsa as News } from '../../models/auto-generated-strapi-types/types/Newsa';
import { serialize } from 'corporate-utils';

interface NewsArchiveProps {
  mainTitle?: string;
  subtitle?: string;
  mainTitleFontSize?: number;
  subtitleFontSize?: number;
  mainTitleMobileFontSize?: number;
  subtitleMobileFontSize?: number;
  subtitleMobileFontWeight?: number;
  subtitleFontWeight?: number;
  mainTitleMobileFontWeight?: number;
  mainTitleFontWeight?: number;
  paginationNews: any;
  news: News[];
  newsAndEventsMerged?: boolean;
  locale: string;
}

const NewsArchive: React.FC<any> = ({
  data,
  translations,
}: {
  data: NewsArchiveProps;
  translations: any;
}) => {
  const router = useRouter();

  const [news] = React.useState(data?.news);

  const [page, setPage] = React.useState(data?.paginationNews?.page || 1);
  const [pageSize, setPageSize] = React.useState(
    data?.paginationNews?.pageSize || 24
  );

  const onChangePage = (page: number): void => {
    setPage(~~page);
    router.push(createPagePath(~~page));
  };

  const createPagePath = (pageToChange: number, size?: number): string => {
    const { query, asPath, locale } = router;
    const path = asPath.split('?')[0];
    const queryCopy = {
      ...query,
    };

    delete queryCopy?.slug;

    const newQuery: any = {
      ...queryCopy,
      page: pageToChange.toString(),
    };

    if (size !== undefined && size !== null) {
      newQuery.pageSize = size;
    }

    return `/${locale}${path}?${serialize(newQuery)}`;
  };

  const onChangePageSize = (size: number): void => {
    setPageSize(~~size);
    setPage(1);
    router.push(createPagePath(1, ~~size));
  };

  const createNewsLink = (row: any): string => {
    return (row?.attributes as any)?.url;
  };

  const goToNews = (row: any): void => {
    router.push(createNewsLink(row));
  };

  if (news)
    return (
      <ContentWrapper>
        {data?.mainTitle && (
          <StyledHeader
            fontSize={data?.mainTitleFontSize}
            fontMobileSize={data?.mainTitleMobileFontSize}
            fontWeight={data?.mainTitleFontWeight}
            fontMobileWeight={data?.mainTitleMobileFontWeight}
          >
            {data?.mainTitle}
          </StyledHeader>
        )}
        {data?.subtitle && (
          <StyledSubtitle
            fontSize={data?.subtitleFontSize}
            fontMobileSize={data?.subtitleMobileFontSize}
            fontWeight={data?.subtitleFontWeight}
            fontMobileWeight={data?.subtitleMobileFontWeight}
          >
            <Markdown>{data?.subtitle}</Markdown>
          </StyledSubtitle>
        )}
        <TableStyled>
          <TableHeader
            data={[
              translations?.pages?.newsArchive?.title,
              translations?.pages?.newsArchive?.date,
              '',
            ]}
          ></TableHeader>
          <TableBody>
            {news &&
              news?.map((row, index) => {
                return (
                  <TableBodyRow key={index} onClick={() => goToNews(row)}>
                    <TableBodyRowCell>
                      {normalizeText(row?.attributes?.title)}
                    </TableBodyRowCell>
                    <TableBodyRowCell>{row?.attributes?.date}</TableBodyRowCell>
                    <TableBodyRowCell>
                      <Link href={createNewsLink(row)} passHref>
                        <StyledLink>
                          {translations?.pages?.newsArchive?.readNews}
                        </StyledLink>
                      </Link>
                    </TableBodyRowCell>
                  </TableBodyRow>
                );
              })}
          </TableBody>
        </TableStyled>
        {news?.length > 0 && (
          <MainPagination>
            <Pagination
              count={Math.ceil(data?.paginationNews?.total / pageSize)}
              page={page || 1}
              onChangePage={onChangePage}
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  href={
                    (!item?.selected &&
                      !item?.disabled &&
                      item?.page &&
                      createPagePath(item?.page)) ||
                    null
                  }
                  onClick={(e: any) => {
                    e.preventDefault();
                    item.onClick();
                  }}
                  component={
                    (!item?.selected && !item?.disabled && 'a') || null
                  }
                />
              )}
            ></Pagination>
            <div>
              <Label>
                {
                  translations?.pages?.category?.main?.products?.pagination
                    ?.label
                }
              </Label>
              <PageSelect
                currentValue={pageSize}
                onChangePageSize={onChangePageSize}
              />
            </div>
          </MainPagination>
        )}
      </ContentWrapper>
    );
  else return <></>;
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug,
  globalLocale: GlobalResponse
) => {
  const news = await getNews({
    locale: slugAnalyzed?.locale,
    pageSize: slugAnalyzed?.query?.pageSize,
    sort: ['date:desc'],
    page: slugAnalyzed?.query?.page,
    datesFormat: translations?.date?.newsArchive?.format,
  });

  if (
    !(
      parseInt(slugAnalyzed?.query?.page as string, 10) >
      news?.meta?.pagination?.pageCount
    )
  ) {
    for (const newss of news.data) {
      newss.attributes.title = normalizeText(newss?.attributes?.title);
    }
    section.news =
      normalizeAttributesNewsEvents(
        news?.data,
        'news',
        translations,
        globalLocale?.attributes?.newsAndEventsMerged || false,
        slugAnalyzed.locale
      ) || [];
    section.paginationNews = news?.meta?.pagination;
    section.newsAndEventsMerged =
      globalLocale?.attributes?.newsAndEventsMerged || false;
    section.locale = slugAnalyzed.locale;
  }
};

export default NewsArchive;
